/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { EditorConfig, LexicalNode, NodeKey, SerializedLexicalNode, Spread } from "lexical";

import { DecoratorNode } from "lexical";
import * as React from "react";
import { Suspense } from "react";

const EquationComponent = React.lazy(
	// @ts-ignore
	() => import("./EquationComponent")
);

export type SerializedEquationNode = Spread<
	{
		type: "equation";
		equation: string;
		inline: boolean;
	},
	SerializedLexicalNode
>;

export class EquationNode extends DecoratorNode<JSX.Element> {
	__equation: string;
	__inline: boolean;

	static getType(): string {
		return "equation";
	}

	static clone(node: EquationNode): EquationNode {
		return new EquationNode(node.__equation, node.__inline, node.__key);
	}

	constructor(equation: string, inline?: boolean, key?: NodeKey) {
		super(key);
		this.__equation = equation;
		this.__inline = inline ?? false;
	}

	static importJSON(serializedNode: SerializedEquationNode): EquationNode {
		const node = $createEquationNode(serializedNode.equation, serializedNode.inline);
		return node;
	}

	exportJSON(): SerializedEquationNode {
		return {
			equation: this.getEquation(),
			inline: this.__inline,
			type: "equation",
			version: 1,
		};
	}

	createDOM(_config: EditorConfig): HTMLElement {
		return document.createElement(this.__inline ? "span" : "div");
	}

	updateDOM(prevNode: EquationNode): boolean {
		// If the inline property changes, replace the element
		return this.__inline !== prevNode.__inline;
	}

	getEquation(): string {
		return this.__equation;
	}

	setEquation(equation: string): void {
		const writable = this.getWritable();
		writable.__equation = equation;
	}

	decorate(): JSX.Element {
		return (
			<Suspense fallback={null}>
				<EquationComponent equation={this.__equation} inline={this.__inline} nodeKey={this.__key} />
			</Suspense>
		);
	}
}

export function $createEquationNode(equation = "", inline = false): EquationNode {
	const equationNode = new EquationNode(equation, inline);
	return equationNode;
}

export function $isEquationNode(node: LexicalNode | null | undefined): node is EquationNode {
	return node instanceof EquationNode;
}
